import type * as React from 'react'
import { useCallback, useContext, useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation } from '@apollo/client/react'
import { getReportedBrowserTimeZone } from '@buffer-mono/popcorn'
import { UserContext } from '../../../../../common/context/User'
import { redirectToUrl } from '../../../../../common/utils/urls'
import {
  CREATE_CUSTOM_CHANNELS,
  UPDATE_CUSTOM_CHANNEL,
} from '../../../../../common/graphql/channels'
import {
  ChannelThirdPartyService,
  ChannelType,
} from '../../../../../gql/graphql'
import { trackCustomChannelSetupViewed } from '../../tracking'
import { useSelector } from 'react-redux'
import type { OrchestratorRootState } from '../../../../../common/events/types'
import trackChannelConnectionStarted from '../../../../../tracking/trackChannelConnectionStarted'

import { setConnectionSuccessQueryParamsForCustomChannel } from '../../utils'
import { isValidHandle, isValidPassword } from './utils/validations'

import { Header, Footer, Body } from './components'
import { ChannelAccountType, Service } from '../../types'

import styles from './BlueskyAuth.module.css'

export default function BlueskyAuth({
  onClose,
  isRefreshingChannelConnection,
  name,
  channelId,
}: {
  onClose: () => void
  isRefreshingChannelConnection: boolean
  name?: string
  channelId?: string
}): React.JSX.Element {
  const {
    selectedService,
    cta,
    prefillDataAfterConnection,
    skipCelebrationModal,
  } = useSelector((state: OrchestratorRootState) => state.channelConnections)
  const [blueskyData, setBlueskyData] = useState({
    handle: name || '',
    appPassword: '',
    followBuffer: true,
  })
  const [errorData, setErrorData] = useState({
    handleError: '',
    appPasswordError: '',
    responseError: '',
  })

  const user = useContext(UserContext)

  // The connection flow either creates a new custom channel or updates an
  // existing one when refreshing. They are distinct typed operations, so each
  // gets its own mutation hook and `handleSubmit` calls the relevant one.
  const [
    createCustomChannels,
    { data: createData, loading: createLoading, error: createError },
  ] = useMutation(CREATE_CUSTOM_CHANNELS)
  const [
    updateCustomChannel,
    { data: updateData, loading: updateLoading, error: updateError },
  ] = useMutation(UPDATE_CUSTOM_CHANNEL)

  const loading = createLoading || updateLoading
  const error = createError ?? updateError

  const createResponse = createData?.createCustomChannels
  const updateResponse = updateData?.updateCustomChannel
  const message =
    (createResponse && 'message' in createResponse
      ? createResponse.message
      : undefined) ||
    (updateResponse && 'message' in updateResponse
      ? updateResponse.message
      : undefined)

  useEffect(() => {
    const newChannelId =
      createResponse && 'channels' in createResponse
        ? createResponse.channels[0]?.id
        : undefined

    if (newChannelId) {
      // Redirect to '/channels' and trigger channel connection success modal.
      // Forward prefillDataAfterConnection / skipCelebrationModal so the
      // composer-reopen path works after a first-channel custom connection —
      // OAuth already does this via useAuthorizationInfo, custom channels
      // must do it here.
      const newURLWithSuccessParams =
        setConnectionSuccessQueryParamsForCustomChannel(newChannelId, {
          prefillDataAfterConnection,
          skipCelebrationModal,
        })
      redirectToUrl(newURLWithSuccessParams.toString())
    } else if (
      updateResponse &&
      'channel' in updateResponse &&
      updateResponse.channel?.id
    ) {
      onClose()
    } else if (message) {
      setErrorData((prevErrorData) => ({
        ...prevErrorData,
        responseError: message,
      }))
    }
  }, [
    createResponse,
    updateResponse,
    message,
    onClose,
    prefillDataAfterConnection,
    skipCelebrationModal,
  ])

  useEffect(() => {
    if (error) {
      setErrorData((prevErrorData) => ({
        ...prevErrorData,
        responseError: error.message,
      }))
    }
  }, [error])

  useEffect(() => {
    if (selectedService) {
      trackCustomChannelSetupViewed({ account: user, service: selectedService })
    }
  }, [selectedService, user])

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      const isValidHandleData = isValidHandle(blueskyData.handle)
      const isValidAppPassword = isValidPassword(
        blueskyData.appPassword,
        isRefreshingChannelConnection,
      )

      if (!isValidHandleData.isValid) {
        setErrorData((prevErrorData) => ({
          ...prevErrorData,
          handleError: isValidHandleData.errorMessage,
        }))
      }

      if (!isValidAppPassword.isValid) {
        setErrorData((prevErrorData) => ({
          ...prevErrorData,
          appPasswordError: isValidAppPassword.errorMessage,
        }))
      }

      if (!isValidHandleData.isValid || !isValidAppPassword.isValid) {
        return
      }

      if (!isRefreshingChannelConnection) {
        trackChannelConnectionStarted({
          payload: {
            service: Service.bluesky,
            channelType: ChannelAccountType.profile,
            cta,
          },
          user,
        })
      }

      if (isRefreshingChannelConnection) {
        updateCustomChannel({
          variables: {
            input: {
              channelId,
              customChannelMetadata: {
                blueskyMetadata: {
                  handle: blueskyData.handle,
                  appPassword: blueskyData.appPassword
                    ? blueskyData.appPassword
                    : undefined,
                },
              },
            },
          },
        })
      } else {
        createCustomChannels({
          variables: {
            input: {
              channels: [
                {
                  organizationId: user?.currentOrganization?.id,
                  service: ChannelThirdPartyService.Bluesky,
                  type: ChannelType.Profile,
                  timezone: getReportedBrowserTimeZone(),
                  customChannelMetadata: {
                    blueskyMetadata: {
                      handle: blueskyData.handle,
                      appPassword: blueskyData.appPassword,
                      followBuffer: blueskyData.followBuffer,
                    },
                  },
                },
              ],
            },
          },
        })
      }
    },
    [
      blueskyData,
      channelId,
      createCustomChannels,
      updateCustomChannel,
      user,
      isRefreshingChannelConnection,
    ],
  )

  return (
    <>
      <Header onClose={onClose} />
      <form className={styles.container} onSubmit={handleSubmit}>
        <Body
          isRefreshingChannelConnection={isRefreshingChannelConnection}
          errorData={errorData}
          blueskyData={blueskyData}
          setErrorData={setErrorData}
          setBlueskyData={setBlueskyData}
        />
        <Footer
          loading={loading}
          blueskyData={blueskyData}
          setBlueskyData={setBlueskyData}
          isRefreshingChannelConnection={isRefreshingChannelConnection}
        />
      </form>
    </>
  )
}
